import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AboutSectionML = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadAboutContent();
  }, [language]);

  const loadAboutContent = async () => {
    setLoading(true);
    const normalizedLang = ['pt','en','fr','ar','zh'].includes(language) ? language : (language.startsWith('pt') ? 'pt' : 'en');
    const result = await getHomepageSectionML('about', normalizedLang);
    if (result.success) setContent(result.data);
    setLoading(false);
  };

  // Build images array from content
  const images = [];
  if (content) {
    if (content.image_url) images.push(content.image_url);
    if (content.carousel_images && Array.isArray(content.carousel_images)) {
      content.carousel_images.forEach(img => { if (img && !images.includes(img)) images.push(img); });
    }
  }
  // Ensure at least 1 image for the carousel
  const hasImages = images.length > 0;

  // Auto-play carousel
  useEffect(() => {
    if (images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % images.length);
      }, 5000);
      return () => clearInterval(intervalRef.current);
    }
  }, [images.length]);

  const goToSlide = (idx) => {
    setCurrentSlide(idx);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % images.length);
    }, 5000);
  };

  const prevSlide = () => goToSlide((currentSlide - 1 + images.length) % images.length);
  const nextSlide = () => goToSlide((currentSlide + 1) % images.length);

  if (loading || !content) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 text-center">
          <div className="animate-pulse">Loading...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Text Content */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-[#00B4D8]" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8]">
                {content.label}
              </p>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#2B2D42] mb-8 leading-[1.15] tracking-tight">
              {content.heading}
            </h2>
            <div className="space-y-5 text-[#6C757D] leading-relaxed text-base">
              {content.paragraphs?.map((paragraph, index) => (
                <p key={index} className={index === 0 ? 'text-lg font-medium text-[#2B2D42]' : ''}>{paragraph}</p>
              ))}
            </div>

            {content.stats && content.stats.length > 0 && (
              <div className="grid grid-cols-2 gap-6 mt-14 pt-8 border-t border-gray-100">
                {content.stats.map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <p className="text-4xl lg:text-5xl font-extrabold text-[#00B4D8] mb-1">{stat.number}</p>
                    <p className="text-sm text-[#6C757D] font-medium uppercase tracking-wide">{stat.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Image Carousel with Zoom-In Effect */}
          <div className="relative">
            {hasImages ? (
              <div className="relative rounded-2xl shadow-2xl overflow-hidden aspect-[4/3]">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
                    style={{ opacity: idx === currentSlide ? 1 : 0 }}
                  >
                    <img
                      src={img}
                      alt={`${content.heading} ${idx + 1}`}
                      className="w-full h-full object-cover"
                      style={{
                        animation: idx === currentSlide ? 'aboutZoomIn 6s ease-out forwards' : 'none',
                        transform: 'scale(1)'
                      }}
                    />
                  </div>
                ))}

                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button onClick={prevSlide}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10">
                      <ChevronLeft className="w-5 h-5 text-[#2B2D42]" />
                    </button>
                    <button onClick={nextSlide}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10">
                      <ChevronRight className="w-5 h-5 text-[#2B2D42]" />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                      {images.map((_, idx) => (
                        <button key={idx} onClick={() => goToSlide(idx)}
                          className={`w-2.5 h-2.5 rounded-full transition-all ${idx === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-[#023E8A] to-[#00B4D8] rounded-2xl shadow-2xl flex items-center justify-center text-white">
                <span className="text-lg">Image Placeholder</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zoom-in keyframes */}
      <style>{`
        @keyframes aboutZoomIn {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }
      `}</style>
    </section>
  );
};

export default AboutSectionML;
